import { diffLines, diffWordsWithSpace } from 'diff';

export type DiffSegment = {
  text: string;
  highlighted: boolean;
};

export type DiffCell = {
  lineNumber: number;
  segments: DiffSegment[];
};

export type DiffRow = {
  type: 'context' | 'change';
  left: DiffCell | null;
  right: DiffCell | null;
};

export const splitLines = (value: string) => {
  const lines = value.split('\n');
  // A chunk ending with '\n' produces a trailing empty string that is not a line.
  if (lines[lines.length - 1] === '') lines.pop();
  return lines;
};

const plainCell = (lineNumber: number, text: string): DiffCell => ({
  lineNumber,
  segments: [{ text, highlighted: false }],
});

// Character-level diff of a paired removed/added line, so only the changed
// parts of the line get the stronger highlight.
const pairCells = (
  leftLineNumber: number,
  leftText: string,
  rightLineNumber: number,
  rightText: string
): { left: DiffCell; right: DiffCell } => {
  const segments = diffWordsWithSpace(leftText, rightText).map(part => {
    if (part.added) {
      return { left: [], right: [{ text: part.value, highlighted: true }] };
    }
    if (part.removed) {
      return { left: [{ text: part.value, highlighted: true }], right: [] };
    }
    return {
      left: [{ text: part.value, highlighted: false }],
      right: [{ text: part.value, highlighted: false }],
    };
  });

  return {
    left: {
      lineNumber: leftLineNumber,
      segments: segments.flatMap(s => s.left),
    },
    right: {
      lineNumber: rightLineNumber,
      segments: segments.flatMap(s => s.right),
    },
  };
};

// Pair up queued removed/added lines index by index into change rows.
const changeRows = (
  removedLines: string[],
  addedLines: string[],
  leftStart: number,
  rightStart: number
): DiffRow[] =>
  Array.from(
    { length: Math.max(removedLines.length, addedLines.length) },
    (_, i): DiffRow => {
      const removed = removedLines[i] ?? null;
      const added = addedLines[i] ?? null;

      if (removed !== null && added !== null) {
        return {
          type: 'change',
          ...pairCells(leftStart + i, removed, rightStart + i, added),
        };
      }
      if (removed !== null) {
        return {
          type: 'change',
          left: {
            lineNumber: leftStart + i,
            segments: [{ text: removed, highlighted: false }],
          },
          right: null,
        };
      }
      return {
        type: 'change',
        left: null,
        right: {
          lineNumber: rightStart + i,
          segments: [{ text: added ?? '', highlighted: false }],
        },
      };
    }
  );

export const computeDiffRows = (
  oldText: string,
  newText: string
): DiffRow[] => {
  const rows: DiffRow[] = [];

  let leftLine = 1;
  let rightLine = 1;
  let pendingRemoved: string[] = [];
  let pendingAdded: string[] = [];

  const flushPending = () => {
    rows.push(...changeRows(pendingRemoved, pendingAdded, leftLine, rightLine));
    leftLine += pendingRemoved.length;
    rightLine += pendingAdded.length;
    pendingRemoved = [];
    pendingAdded = [];
  };

  diffLines(oldText, newText).forEach(change => {
    const lines = splitLines(change.value);

    if (change.removed) {
      pendingRemoved.push(...lines);
    } else if (change.added) {
      pendingAdded.push(...lines);
    } else {
      flushPending();
      lines.forEach(line => {
        rows.push({
          type: 'context',
          left: plainCell(leftLine, line),
          right: plainCell(rightLine, line),
        });
        leftLine += 1;
        rightLine += 1;
      });
    }
  });

  flushPending();

  return rows;
};

// Indices of rows that start a contiguous block of changes, used for
// prev/next navigation and the change counter.
export const getChangeBlockStarts = (rows: DiffRow[]): number[] =>
  rows.flatMap((row, index) =>
    row.type === 'change' && rows[index - 1]?.type !== 'change' ? [index] : []
  );

export type Fold = {
  // Row index range [start, end) of foldable context rows.
  start: number;
  end: number;
};

// Runs of context rows are foldable, except `contextLines` rows kept visible
// next to each change block (and nothing is kept at the very start/end of the
// document, matching GitLab's behavior). Runs too short to be worth folding
// stay visible.
export const computeFolds = (
  rows: DiffRow[],
  contextLines: number,
  minFoldSize: number
): Fold[] => {
  const folds: Fold[] = [];

  let runStart: number | null = null;

  const closeRun = (runEnd: number) => {
    if (runStart === null) return;
    const foldStart = runStart === 0 ? 0 : runStart + contextLines;
    const foldEnd = runEnd === rows.length ? runEnd : runEnd - contextLines;
    if (foldEnd - foldStart >= minFoldSize) {
      folds.push({ start: foldStart, end: foldEnd });
    }
    runStart = null;
  };

  rows.forEach((row, index) => {
    if (row.type === 'context') {
      if (runStart === null) runStart = index;
    } else {
      closeRun(index);
    }
  });
  closeRun(rows.length);

  return folds;
};
